'use client';

import { useCallback, useRef } from 'react';

import { useMount } from './useMount';

export function useDebounce<Params, ReturnValue>(
  callback: (...params: Params[]) => ReturnValue,
  wait: number,
) {
  const timerRef = useRef<number | null>(null);

  const debouncedFunc = useCallback(
    (...params: Params[]) => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
      }

      timerRef.current = window.setTimeout(() => callback(...params), wait);
    },
    [callback, wait],
  );

  useMount(() => {
    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
      }
    };
  });

  const clear = useCallback(() => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  return {
    clear,
    debouncedFunc,
  };
}
